import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { LanguageSwitcher } from '../components/i18n/LanguageSwitcher'
import { getKata, type KataId } from '../data/catalog'
import { useI18n } from '../i18n'
import { kataBannerBackground } from '../lib/theme/kataBanner'
import { YoutubePlayer, type YoutubePlayerHandle } from '../lib/youtube/YoutubePlayer'
import { useUnsavedGuard } from '../lib/session/unsavedGuard'
import {
  parseYoutubeUrl,
  youtubeUrlErrorKey,
  type YoutubeUrlErrorCode,
} from '../lib/youtube/parseUrl'
import {
  getActiveReference,
  getPreferences,
  isYoutubeDataSaverEnabled,
  removeReference,
  saveReference,
} from '../storage/repositories/references'
import styles from './VideoSetup.module.css'

export function VideoSetup() {
  const { t } = useI18n()
  const { kataId } = useParams<{ kataId: string }>()
  const kata = kataId ? getKata(kataId as KataId) : undefined
  const navigate = useNavigate()
  const playerRef = useRef<YoutubePlayerHandle>(null)

  const [url, setUrl] = useState('')
  const [title, setTitle] = useState('')
  const [previewId, setPreviewId] = useState<string | null>(null)
  /** Iframe loads only after Preview — avoids streaming on setup open. */
  const [loadPlayer, setLoadPlayer] = useState(false)
  const [urlError, setUrlError] = useState<YoutubeUrlErrorCode | null>(null)
  const [playerError, setPlayerError] = useState<string | null>(null)
  const [existingId, setExistingId] = useState<string | undefined>()
  const [existingVideoId, setExistingVideoId] = useState<string | undefined>()
  const [existingSegmentCount, setExistingSegmentCount] = useState(0)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [identityWarning, setIdentityWarning] = useState(false)
  const [confirmReplace, setConfirmReplace] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [dataSaverEnabled, setDataSaverEnabled] = useState(true)

  useUnsavedGuard(dirty)

  useEffect(() => {
    if (!kata) return
    let cancelled = false
    ;(async () => {
      const [ref, prefs] = await Promise.all([getActiveReference(kata.id), getPreferences()])
      if (cancelled) return
      setDataSaverEnabled(isYoutubeDataSaverEnabled(prefs))
      if (ref) {
        setExistingId(ref.id)
        setExistingVideoId(ref.videoId)
        setExistingSegmentCount(ref.segments.length)
        setTitle(ref.title)
        setUrl(`https://www.youtube.com/watch?v=${ref.videoId}`)
        setPreviewId(ref.videoId)
        // Immediate load only when data saver is off.
        if (!isYoutubeDataSaverEnabled(prefs)) {
          setLoadPlayer(true)
        }
      }
      setLoaded(true)
    })()
    return () => {
      cancelled = true
    }
  }, [kata])

  useEffect(() => {
    if (!dirty) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty])

  if (!kataId || !kata) {
    return <Navigate to="/" replace />
  }

  // Poster from the typed URL without creating the iframe until Preview.
  const typedParse = parseYoutubeUrl(url)
  const playerVideoId = previewId ?? (typedParse.ok ? typedParse.videoId : null)

  const evaluateIdentity = (videoId: string) => {
    if (existingVideoId && videoId !== existingVideoId) {
      setIdentityWarning(true)
      setConfirmReplace(false)
    } else {
      setIdentityWarning(false)
      setConfirmReplace(false)
    }
  }

  const handlePreview = () => {
    setPlayerError(null)
    const parsed = parseYoutubeUrl(url)
    if (!parsed.ok) {
      setUrlError(parsed.error)
      // Do not damage prior preview / saved settings on invalid parse.
      return
    }
    setUrlError(null)
    setPreviewId(parsed.videoId)
    setLoadPlayer(true)
    evaluateIdentity(parsed.videoId)
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setSaveError(null)
    const parsed = parseYoutubeUrl(url)
    if (!parsed.ok) {
      setUrlError(parsed.error)
      return
    }
    setUrlError(null)

    const videoChanged = Boolean(existingVideoId && existingVideoId !== parsed.videoId)
    if (videoChanged) {
      setIdentityWarning(true)
      setPreviewId(parsed.videoId)
      if (!confirmReplace) {
        setConfirmReplace(true)
        return
      }
    }

    setSaving(true)
    try {
      const saved = await saveReference({
        id: existingId,
        kataId: kata.id,
        title: title.trim() || t('setup.titlePlaceholder', { kata: kata.name }),
        videoId: parsed.videoId,
        // Replacing video identity must never silently reuse previous timings.
        clearSegments: videoChanged,
        segments: videoChanged ? [] : undefined,
      })
      setDirty(false)
      navigate(`/kata/${kata.id}/edit`, { state: { savedReferenceId: saved.id } })
    } catch {
      setSaveError(t('setup.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  const handleRemove = async () => {
    if (!existingId) return
    const ok = window.confirm(t('setup.removeConfirm', { kata: kata.name }))
    if (!ok) return
    setRemoving(true)
    setSaveError(null)
    try {
      await removeReference(existingId)
      setDirty(false)
      navigate(`/kata/${kata.id}`)
    } catch {
      setSaveError(t('setup.removeFailed'))
    } finally {
      setRemoving(false)
    }
  }

  const handleCancel = () => {
    if (dirty && !window.confirm(t('setup.discardConfirm'))) return
    navigate(`/kata/${kata.id}`)
  }

  const isReplace = Boolean(existingId)
  const primaryLabel = saving
    ? t('common.saving')
    : identityWarning && !confirmReplace
      ? t('setup.confirmReplace')
      : isReplace
        ? t('setup.saveReference')
        : t('setup.continue')

  return (
    <div className="app-shell">
      <div className="page-header">
        <Link to={`/kata/${kata.id}`} className="btn btn-ghost">
          {t('common.back')}
        </Link>
        <h1>{isReplace ? t('setup.editTitle') : t('setup.configureTitle')}</h1>
        <LanguageSwitcher compact />
      </div>

      <div
        className={styles.banner}
        style={{
          background: kataBannerBackground(kata.theme),
          color: kata.theme.text,
        }}
      >
        <strong lang="ja">{kata.nameJa}</strong>
        <span className={styles.bannerRomaji}>{kata.name}</span>
        <span>{t('setup.bannerHint')}</span>
      </div>

      {!loaded ? (
        <p className="banner banner-info" role="status">
          {t('common.loading')}
        </p>
      ) : (
        <form className={`surface ${styles.form}`} onSubmit={(e) => void handleSubmit(e)}>
          <div className="field">
            <label htmlFor="yt-url">{t('setup.urlLabel')}</label>
            <input
              id="yt-url"
              type="url"
              placeholder="https://www.youtube.com/watch?v=…"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value)
                setDirty(true)
                setUrlError(null)
                setConfirmReplace(false)
                setIdentityWarning(false)
                setLoadPlayer(false)
              }}
              aria-invalid={Boolean(urlError)}
              aria-describedby={urlError ? 'yt-url-error' : 'yt-url-hint'}
              required
            />
            {urlError ? (
              <span id="yt-url-error" className="field-error">
                {t(youtubeUrlErrorKey(urlError))}
              </span>
            ) : (
              <span id="yt-url-hint" className="field-hint">
                {t('setup.urlHint')}
              </span>
            )}
          </div>

          <div className="field">
            <label htmlFor="yt-title">{t('setup.titleLabel')}</label>
            <input
              id="yt-title"
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value)
                setDirty(true)
              }}
              placeholder={t('setup.titlePlaceholder', { kata: kata.name })}
            />
            <span className="field-hint">{t('setup.titleHint')}</span>
          </div>

          <div className={styles.previewActions}>
            <button type="button" className="btn btn-secondary" onClick={handlePreview}>
              {t('setup.preview')}
            </button>
            {dirty && (
              <span className="banner banner-warning" role="status">
                {t('setup.unsaved')}
              </span>
            )}
          </div>

          {identityWarning && (
            <p className="banner banner-warning" role="alert">
              {t('setup.identityWarning', {
                timings:
                  existingSegmentCount > 0
                    ? t('setup.timingsCount', { count: existingSegmentCount })
                    : '',
              })}
            </p>
          )}

          {playerError && (
            <p className="banner banner-error" role="alert">
              {playerError}
            </p>
          )}

          <YoutubePlayer
            ref={playerRef}
            videoId={playerVideoId}
            title={title || kata.name}
            deferLoad={dataSaverEnabled && !loadPlayer}
            onError={setPlayerError}
          />

          {saveError && (
            <p className="banner banner-error" role="alert">
              {saveError}
            </p>
          )}

          <div className={styles.footer}>
            {existingId && (
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => void handleRemove()}
                disabled={saving || removing}
              >
                {removing ? t('setup.removing') : t('setup.removeVideo')}
              </button>
            )}
            <div className={styles.footerRight}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
                disabled={saving || removing}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving || removing || !url.trim()}
              >
                {primaryLabel}
              </button>
            </div>
          </div>
        </form>
      )}

      <p className="disclaimer">{t('setup.disclaimer')}</p>
    </div>
  )
}
